
import { computed, defineComponent, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useNotificationStore } from '@/store/stores/notification'
import { Notification } from '@/types/main'

const NOTIFICATION_HEIGHT_EVENT = 'dh:notification-height'

export default defineComponent({
    props: {
        readedNotifications: {
            type: Array,
            required: true 
        },
        default: () => [],
    },
    setup(props, context) {
        const notificationStore = useNotificationStore()
        const barRoot = ref<HTMLElement | null>(null)
        const dismissedNotificationIds = ref<number[]>([])

        const sortedNotifications = computed(() => {
            return [...(props.readedNotifications as Notification[])].sort((a, b) => {
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            })
        })

        const activeNotifications = computed(() => {
            const dismissedSet = new Set(dismissedNotificationIds.value)
            return sortedNotifications.value.filter(notification => !dismissedSet.has(notification.id))
        })

        // Всегда отображаем максимум два последних уведомления;
        // новые приходят вниз, более старые скрываются сверху.
        const visibleNotifications = computed(() => activeNotifications.value.slice(-2))

        const publishNotificationHeight = (height: number) => {
            const px = `${Math.max(0, Math.round(height))}px`
            document.documentElement.style.setProperty('--app-notification-height', px)
            window.dispatchEvent(
                new CustomEvent(NOTIFICATION_HEIGHT_EVENT, { detail: { height: Math.max(0, height) } })
            )
        }

        const updateNotificationHeightVar = () => {
            const barRootAny = barRoot.value as any
            const barElement = barRootAny?.$el || barRootAny
            const height = visibleNotifications.value.length
                ? barElement?.offsetHeight || 0
                : 0
            publishNotificationHeight(height)
        }

        const dismissNotification = (id: number) => {
            if (!dismissedNotificationIds.value.includes(id)) {
                dismissedNotificationIds.value = [...dismissedNotificationIds.value, id]
            }
        }
        const openNotification = (id: number) => {
            notificationStore.unReadreadNotification(id)
        }

        const plainText = (value: string) => {
            return value ? value.replace(/<\/?.+?>/gi, '') : ''
        }
        const fullText = (notification: Notification) => {
            const body = plainText(notification.text)
            return body ? `${notification.title} ${body}` : notification.title
        }

        const iconSymbol = (severity: string) => (severity === 'dangerous' ? '!' : 'i')
        const onRowsTransitionEnd = () => nextTick(() => updateNotificationHeightVar())

        watch(visibleNotifications, () => nextTick(() => updateNotificationHeightVar()), { deep: true })
        watch(
            sortedNotifications,
            notifications => {
                const existingIds = new Set(notifications.map(notification => notification.id))
                dismissedNotificationIds.value = dismissedNotificationIds.value.filter(id =>
                    existingIds.has(id)
                )
            },
            { deep: true }
        )

        onMounted(() => {
            nextTick(() => updateNotificationHeightVar())
            window.addEventListener('resize', updateNotificationHeightVar)
        })

        onUnmounted(() => {
            window.removeEventListener('resize', updateNotificationHeightVar)
            publishNotificationHeight(0)
        })

        return {
            barRoot,
            visibleNotifications,
            dismissNotification,
            openNotification,
            plainText,
            fullText,
            iconSymbol,
            onRowsTransitionEnd,
        }
    },
})
